const CUT_OFF_MARKER = '…';

interface TextAsset {
  text?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function endsCutOff(text: unknown): boolean {
  return typeof text === 'string' && text.trimEnd().endsWith(CUT_OFF_MARKER);
}

function assetList(value: unknown): TextAsset[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

/**
 * The fields of a stored `ad_units.copy` payload whose text ends in an ellipsis.
 *
 * An ad written to fit a character limit does not end in an ellipsis: the limit is met by writing
 * fewer words. A stored line that ends in one was shortened by code, so it stops mid-sentence and
 * reads as the ad. This only reads the stored payload; nothing is rewritten. Meta, LinkedIn and
 * Google keep their text in different keys, so each key is checked where it exists and an unknown
 * shape reports nothing rather than guessing.
 */
export function copyCutOffFields(copy: unknown): string[] {
  if (!isRecord(copy)) return [];
  const found: string[] = [];

  for (const key of ['headline', 'commentary', 'description'] as const) {
    if (endsCutOff(copy[key])) found.push(key === 'commentary' ? 'text' : key);
  }
  const lists = [
    ['headlines', 'headline'],
    ['descriptions', 'description'],
    ['primaryTexts', 'text'],
  ] as const;
  for (const [key, label] of lists) {
    assetList(copy[key]).forEach((asset, index) => {
      if (endsCutOff(asset.text)) found.push(`${label} ${index + 1}`);
    });
  }
  return found;
}

export function hasCutOffCopy(copy: unknown): boolean {
  return copyCutOffFields(copy).length > 0;
}
